import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Highcharts from 'highcharts';
import HC_exporting from 'highcharts/modules/exporting';
import HighchartsReact from 'highcharts-react-official'

import {
    ThreatDBIOC
} from '../../../../Templates/ThreatDBIOC'

import {
    Select,
    Space,
    Button,
    Card,
    Spin,
    Typography,
    Row,
    Col,
    Table
} from "antd";
import { DiventryBlock } from "../../../../Templates/Layout";



const statColors = {
    all: '#3498db',
    blocking: "#e74c3c",
    detecting: '#f1c40f',
    passing: '#16a085',
};

HC_exporting(Highcharts);

export function Statistics({ kernel }) {
    const { t } = useTranslation();
    const { id } = useParams();

    const periodes = [
        { value: 'last24h', label: t('Statistics.last24h') },
        { value: 'last30d', label: t('Statistics.last30d') },
        { value: 'last90d', label: t('Statistics.last90d') },
    ]

    const initialGlobaux = [
        {
            name: 'Statistics.requests',
            value: 0,
            color: '#000000'
        },
        {
            name: 'Statistics.blockedRequests',
            value: 0,
            color: '#941010cf'
        },
        {
            name: 'Statistics.blockedRequestsPercent',
            value: 0,
            color: '#777777',
            unit: '%'
        }
    ];

    const [loading, setLoading] = useState(false);
    const [globalStats, setGlobalStats] = useState(initialGlobaux);
    const [requestsLine, setRequestsLine] = useState([]);
    const [tagsPie, setTagsPie] = useState([]);
    const [topDomains, setTopDomains] = useState([]);
    const [tags, setTags] = useState([]);

    const [filters, setFilters] = useState({ tag: "all", periode: 'last24h' });

    useEffect(() => {
        asyncFetch();
    }, []);

    const asyncFetch = (periode = filters?.periode) => {
        (async () => {
            setLoading(true);

            const context = {}

            // fetch tags
            {
                const raw = await kernel.api.get('/threatdb/tags/list')
                if (raw.error) {
                    setTags({})
                }
                else {
                    context.tags = {}
                    if (Array.isArray(raw?.data)) {
                        raw.data.map((tag) => {
                            context.tags[tag.name] = tag
                        })
                    }
                    setTags(context.tags)
                }
            }

            // fetch request stats
            {
                const globaux = await kernel.api.get(`/dohzel/stats/requests?deviceId=${id}&period=${periode}`)
                if (!globaux.error) {
                    // feed global stats
                    const _globalStats = [];
                    for (var clone of initialGlobaux)
                        _globalStats.push({ ...clone })
                    _globalStats[0].value = globaux?.data?.values?.reduce((acc, item) => acc + item?.[1], 0) ?? 0;
                    _globalStats[1].value = globaux?.data?.values?.reduce((acc, item) => acc + item?.[2], 0) ?? 0;
                    _globalStats[2].value = (_globalStats[1].value * 100 / _globalStats[0].value).toFixed(2);
                    setGlobalStats(_globalStats)

                    // prepare lines chart
                    const statTypes = {
                        all: {
                            name: "Statistics.requests",
                            color: statColors.all
                        },
                        blocking: {
                            name: "Statistics.blockedRequests",
                            color: statColors.blocking
                        },
                    }

                    const requests = []
                    Object.keys(statTypes).forEach((stat, index) => {
                        requests.push({
                            name: t(statTypes?.[stat]?.name),
                            data: globaux.data.values?.map(item => ([new Date(item[0]).getTime(), item[index + 1]])),
                            step: true,
                            color: statTypes?.[stat]?.color,
                            // visible: !disabled?.includes(stat) ?? true
                        })
                    });
                    setRequestsLine(requests)

                }
            }

            // fetch tags stats
            {
                const raw = await kernel.api.get(`/dohzel/stats/tags?deviceId=${id}&period=${periode}`)
                if (!raw.error) {
                    // compute values
                    const values = {}

                    for (var pt of raw.data.values) {
                        const key = pt[1]
                        const ctag = context.tags[key]
                        if (!ctag)
                            continue
                        if (!values[key]) {
                            values[key] = {
                                name: ctag.name,
                                y: 0,
                                color: ctag.color
                            }
                        }
                        values[key].y += pt[2]
                    }

                    setTagsPie(Object.values(values))
                }
            }

            // fetch top domains
            {
                const raw = await kernel.api.get(`/dohzel/stats/top/domains?deviceId=${id}&period=${periode}`)
                if (!raw.error) {
                    setTopDomains(raw.data.values)
                }
            }

            setLoading(false);
        })();
    };

    const topDomainsColumns = [
        {
            title: t('Statistics.domain'),
            dataIndex: 'domain',
            key: 'domain',
            render: (text, record) => (
                <ThreatDBIOC key={`${record.domain}-ioc`} kernel={kernel} value={record.domain} />
            ),
        },
        {
            title: t('Statistics.blocked'),
            dataIndex: 'count',
            key: 'count',
            sorter: (a, b) => a.count - b.count,
            defaultSortOrder: 'descend',
        }
    ];

    return (
        <DiventryBlock>
            <Space.Compact style={{ paddingBottom: 10 }}>
                <Button onClick={() => {
                    asyncFetch();
                }}>
                    {t(`Refresh`)}
                </Button>

                <Select
                    value={filters.periode}
                    style={{ minWidth: 200 }}
                    onChange={(key) => {
                        setFilters({ ...filters, periode: key });
                        asyncFetch(key);
                    }}
                    options={periodes}
                />
            </Space.Compact>


            <Row gutter={[16, 16]}>
                {
                    globalStats?.length ?
                        <>
                            {globalStats.map((stat, index) => (
                                <Col
                                    key={index}
                                    xs={24}   // 1 par ligne sur petit écran
                                    sm={12}   // 2 par ligne sur écran moyen
                                    md={8}    // 3 par ligne à partir de medium (md ≥ 768px)
                                >
                                    <Card>
                                        <h1 style={{ ...styles.statName, color: stat.color }}>
                                            {new Intl.NumberFormat("fr-FR").format(stat.value)} {stat.unit}
                                        </h1>
                                        <span style={styles.statValue}>{t(`${stat.name}`)}</span>
                                    </Card>
                                </Col>
                            ))}
                        </>
                        :
                        <div style={{ height: 400, paddingTop: 200, width: "100%", textAlign: "center" }}>
                            <Spin spinning={true} tip="Loading" size="large" />
                        </div>
                }


                <Col span={24}>
                    <Card style={{ ...styles.statContainer, textAlign: 'left' }}>
                        {loading === false ?
                            <HighchartsReact
                                highcharts={Highcharts}
                                options={{
                                    chart: {
                                        type: 'spline'
                                    },
                                    title: null,
                                    xAxis: {
                                        type: 'datetime',
                                    },
                                    yAxis: {
                                        title: {
                                            text: 'Request'
                                        }
                                    },
                                    legend: {
                                        align: 'left',
                                        verticalAlign: 'top',
                                        borderWidth: 0
                                    },

                                    tooltip: {
                                        shared: true,
                                        crosshairs: true
                                    },
                                    plotOptions: {
                                        area: {
                                            fillOpacity: 0
                                        }
                                    },
                                    exporting: {
                                        enabled: false
                                    },
                                    credits: {
                                        enabled: false
                                    },
                                    series: requestsLine
                                }}
                            />
                            :
                            <div style={{ height: 400, paddingTop: 200, width: "100%", textAlign: "center" }}>
                                <Spin spinning={true} tip="Loading" size="large" />
                            </div>
                        }

                    </Card>
                </Col>
                <Col span={12}>
                    <Card style={{ ...styles.pieContainer, textAlign: 'left' }}>
                        {loading === false ?
                            <HighchartsReact
                                highcharts={Highcharts}
                                options={{
                                    chart: {
                                        type: 'pie',
                                        options3d: {
                                            enabled: true,
                                            alpha: 45
                                        }
                                    },
                                    title: {
                                        text: '',
                                        align: 'left'
                                    },
                                    subtitle: {
                                        text: '',
                                        align: 'left'
                                    },
                                    exporting: {
                                        enabled: false
                                    },
                                    plotOptions: {
                                        pie: {
                                            depth: 45
                                        }
                                    },
                                    credits: {
                                        enabled: false
                                    },
                                    series: [{
                                        name: t('Statistics.event'),
                                        data: tagsPie
                                    }],
                                }}
                            />
                            :
                            <div style={{ height: 400, paddingTop: 200, width: "100%", textAlign: "center" }}>
                                <Spin spinning={true} tip="Loading" size="large" />
                            </div>
                        }
                    </Card>
                </Col>

                <Col span={12}>
                    <Card
                        title={
                            <Typography.Title level={4}>{t('Statistics.topDomains')}</Typography.Title>
                        }
                        style={{ ...styles.tableContainer }}
                    >
                        {loading === false ?
                            <Table
                                size="small"
                                dataSource={topDomains}
                                columns={topDomainsColumns}
                                // loading={loading}
                                pagination={false}
                                rowKey="domain"
                            />
                            :
                            <div style={{ height: 400, paddingTop: 200, width: "100%", textAlign: "center" }}>
                                <Spin spinning={true} tip="Loading" size="large" />
                            </div>
                        }
                    </Card>
                </Col>
            </Row>

        </DiventryBlock>
    );
}


const styles = {
    filters: {
        marginTop: 20
    },
    statContainer: {
        // marginTop: 25,
        textAlign: 'center'
    },
    pieContainer: {
        // marginTop: 25,
        textAlign: 'center'
    },
    tableContainer: {
        textAlign: 'left'
    },
    statName: {
        marginBottom: '-0.2em',
        fontWeight: 800
    },
    statValue: {
        color: '#777777',
        fontSize: '18px'
    }
}